import * as THREE from 'three';
import {
  Spring, Spring3, Pose, AdditiveStack, driftNoise,
  clamp01, lerp, smoothstep, easeOutCubic, easeInOutCubic,
} from './Anim.js';

/**
 * Viewmodel rig and layered blend system.
 *
 * Compositing order, once per rendered frame, never varies:
 *
 *   base  --(sprint weight)-->  sprint  --(ads weight)-->  ads      [replacement]
 *   + bob + sway/lag + breathe + recoil + sequence offset            [additive]
 *
 * Replacement layers are slerped rigid transforms; additive layers accumulate into a
 * single offset applied around a wrist pivot, so ADS, firing and bob compose instead
 * of fighting. At ads = 1 with the additive stack settled the transform is *exactly*
 * the sight-aligned pose, which is what makes the reticle land on the pixel centre.
 */

const _m = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler(0, 0, 0, 'XYZ');
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();

export class WeaponInstance {
  constructor(name, def, built, meta) {
    this.name = name;
    this.def = def;
    this.group = built.group;
    this.skeleton = built.skeleton;
    this.boneByName = built.boneByName;
    this.meshes = built.meshes;
    this.meta = meta;
    this.rest = new Map();
    for (const [n, b] of built.boneByName) {
      this.rest.set(n, { pos: b.position.clone(), quat: b.quaternion.clone() });
    }
    this.group.visible = false;

    // ammo
    this.magSize = def.magSize;
    this.mag = def.magSize;
    this.reserve = def.reserve;
    this.chambered = true;
    this.fireModeIndex = 0;
    /** Muzzle device state. Audio picks its gunshot variant from this. */
    this.suppressed = !!def.suppressed;
  }

  get fireMode() { return this.def.fireModes[this.fireModeIndex]; }

  /**
   * The public descriptor carried by every `weapon:*` event.
   *
   * `class` is one of `rifle | smg | shotgun | pistol | sniper` — audio maps it
   * straight onto its gunshot bank, and the HUD prints `displayName`. It also
   * stringifies to the internal weapon name so any consumer written against the
   * old string payload keeps working unchanged.
   */
  info() {
    const d = this.def;
    const name = this.name;
    return {
      name,
      displayName: d.label ?? name,
      class: d.class,
      suppressed: !!this.suppressed,
      fireMode: this.fireMode,
      fireModes: d.fireModes.slice(),
      magSize: this.magSize,
      rpm: d.rpm ?? 0,
      slot: d.slot ?? 0,
      toString() { return name; },
    };
  }

  bone(n) { return this.boneByName.get(n); }

  resetPose() {
    for (const [n, b] of this.boneByName) {
      const r = this.rest.get(n);
      b.position.copy(r.pos);
      b.quaternion.copy(r.quat);
    }
  }

  /** Local offset applied on top of the bind pose. */
  poseBone(n, pos, rotAxis, rotAngle) {
    const b = this.boneByName.get(n);
    if (!b) return;
    const r = this.rest.get(n);
    if (pos) b.position.copy(r.pos).add(pos);
    if (rotAngle) b.quaternion.copy(r.quat).multiply(_q.setFromAxisAngle(rotAxis, rotAngle));
  }

  translateBone(n, x, y, z) {
    const b = this.boneByName.get(n);
    if (!b) return;
    const r = this.rest.get(n);
    b.position.set(r.pos.x + x, r.pos.y + y, r.pos.z + z);
  }

  rotateBone(n, axis, angle) {
    const b = this.boneByName.get(n);
    if (!b) return;
    const r = this.rest.get(n);
    b.quaternion.copy(r.quat).multiply(_q.setFromAxisAngle(axis, angle));
  }

  /** Rotate in the PARENT's frame (weapon space for anything parented to root). */
  rotateBoneParent(n, axis, angle) {
    const b = this.boneByName.get(n);
    if (!b) return;
    const r = this.rest.get(n);
    b.quaternion.copy(r.quat).premultiply(_q.setFromAxisAngle(axis, angle));
  }

  /** Rotate about an arbitrary pivot expressed in the parent's frame. */
  pivotBone(n, pivot, axis, angle, extra) {
    const b = this.boneByName.get(n);
    if (!b) return;
    const r = this.rest.get(n);
    _q.setFromAxisAngle(axis, angle);
    b.position.copy(r.pos).sub(pivot).applyQuaternion(_q).add(pivot);
    if (extra) b.position.add(extra);
    b.quaternion.copy(r.quat).premultiply(_q);
  }
}

const AX = new THREE.Vector3(1, 0, 0);
const AY = new THREE.Vector3(0, 1, 0);
const AZ = new THREE.Vector3(0, 0, 1);

export class Viewmodel {
  constructor(ctx) {
    this.ctx = ctx;

    this.root = new THREE.Group();
    this.root.name = 'viewmodelRoot';
    this.root.matrixAutoUpdate = false;

    this.rig = new THREE.Group();
    this.rig.name = 'viewmodelRig';
    this.rig.matrixAutoUpdate = false;
    this.root.add(this.rig);

    this.instances = new Map();
    this.current = null;

    // --- blend weights -----------------------------------------------------
    this.ads = 0;
    this.adsTarget = 0;
    this.sprint = 0;
    this.sprintTarget = 0;
    this.tacSprint = 0;
    this.lower = 0;

    // --- additive springs --------------------------------------------------
    this.swayPos = new Spring3(15);
    this.swayRot = new Spring3(13);
    this.bobPos = new THREE.Vector3();
    this.bobRot = new THREE.Vector3();
    this.recoilPos = new Spring3(26);
    this.recoilRot = new Spring3(24);
    this.jumpSpring = new Spring(11);
    this.add = new AdditiveStack();

    this.breathT = 0;
    this.bobPhase = 0;
    this.lagSmooth = new THREE.Vector2();

    // --- sequence ----------------------------------------------------------
    this.seq = null;
    this.seqT = 0;
    this.seqOffset = { pos: new THREE.Vector3(), rot: new THREE.Vector3() };

    this.pivot = new THREE.Vector3(0.075, -0.055, 0.085);
    this._poseA = new Pose();
    this._poseB = new Pose();
    this.fovBase = 60;
    this.fov = 60;
  }

  addWeapon(inst) {
    this.instances.set(inst.name, inst);
    this.rig.add(inst.group);
    return inst;
  }

  equip(name) {
    const inst = this.instances.get(name);
    if (!inst) return null;
    /*
     * Hide EVERY other instance, not just the outgoing one.
     *
     * `THREE.Group.visible` defaults to true, and only the weapon that has been
     * equipped at least once was ever hidden — so from boot until its first
     * equip, every un-equipped weapon was being drawn on top of the one in
     * hand. `wpn_pistol` rendered the rifle; `wpn_rifle_hip` drew 4 457 k
     * triangles against the rifle's own 4 053 k. Draw calls and triangles for
     * the whole viewmodel path were measuring the other three guns too.
     */
    for (const other of this.instances.values()) other.group.visible = (other === inst);
    this.current = inst;
    inst.resetPose();
    return inst;
  }

  /* ------------------------------------------------------------------ *
   * Poses
   * ------------------------------------------------------------------ */

  /** The exact sight-aligned transform: the reticle lands on the optical axis. */
  adsPose(inst, out) {
    const s = inst.meta.sight;
    const relief = inst.def.eyeRelief ?? 0.16;
    const roll = inst.def.adsRoll ?? 0;
    out.quat.setFromAxisAngle(AZ, roll);
    // rig * (0, axis, sightZ) must equal (0, 0, -relief)
    _v.set(0, s.axis, s.z).applyQuaternion(out.quat);
    out.pos.set(-_v.x, -_v.y, -relief - _v.z);
    return out;
  }

  hipPose(inst, out) {
    const p = inst.def.hip;
    out.pos.set(p.pos[0], p.pos[1], p.pos[2]);
    _e.set(p.rot[0], p.rot[1], p.rot[2], 'XYZ');
    out.quat.setFromEuler(_e);
    return out;
  }

  sprintPose(inst, out, tac) {
    const p = tac ? (inst.def.tacSprint ?? inst.def.sprint) : inst.def.sprint;
    out.pos.set(p.pos[0], p.pos[1], p.pos[2]);
    _e.set(p.rot[0], p.rot[1], p.rot[2], 'XYZ');
    out.quat.setFromEuler(_e);
    return out;
  }

  /* ------------------------------------------------------------------ *
   * Per-frame update
   * ------------------------------------------------------------------ */

  /**
   * @param {number} dt
   * @param {{speed:number, sprinting:boolean, tacSprint:boolean, grounded:boolean,
   *          crouched:boolean, moving:number, lookDx:number, lookDy:number,
   *          bob:THREE.Vector3|null, sway:{x:number,y:number}|null}} s
   */
  update(dt, s) {
    const inst = this.current;
    if (!inst) return;
    const def = inst.def;

    /* ---- blend weights ---- */
    const adsRate = 1 / Math.max(0.02, def.adsTime ?? 0.22);
    if (Math.abs(this.adsTarget - this.ads) <= dt * adsRate) this.ads = this.adsTarget;
    else this.ads = THREE.MathUtils.clamp(
      this.ads + Math.sign(this.adsTarget - this.ads) * dt * adsRate, 0, 1);

    const sprintRate = 1 / (def.sprintTime ?? 0.20);
    this.sprint = THREE.MathUtils.clamp(
      this.sprint + Math.sign(this.sprintTarget - this.sprint) * dt * sprintRate, 0, 1);
    this.tacSprint = THREE.MathUtils.clamp(
      this.tacSprint + Math.sign((s.tacSprint ? 1 : 0) - this.tacSprint) * dt * sprintRate, 0, 1);

    const wSprint = this.sprint * (1 - this.ads);
    const wAds = this.ads;

    /* ---- replacement chain ---- */
    const A = this._poseA, Bp = this._poseB;
    this.hipPose(inst, A);
    if (wSprint > 0.0005) {
      this.sprintPose(inst, Bp, this.tacSprint > 0.5);
      Pose.blend(A, A, Bp, smoothstep(wSprint));
    }
    if (wAds > 0.0005) {
      this.adsPose(inst, Bp);
      Pose.blend(A, A, Bp, smoothstep(wAds));
    }

    /* ---- additive: breathing ---- */
    this.add.clear();
    this.breathT += dt;
    const heavy = def.weight ?? 1;
    // Breath is fully suppressed at ads = 1 (breath hold), which is both the COD
    // behaviour and what makes the settled sight picture pixel-exact.
    const breathScale = (1 - this.ads) * (1 - wSprint * 0.6);
    driftNoise(_v, this.breathT / heavy, 0.0028 * breathScale * heavy, 0);
    driftNoise(_v2, this.breathT / heavy + 37.5, 0.0135 * breathScale, 11.3);
    this.add.addVec(_v, _v2);

    /* ---- additive: sway / lag from mouse + view sway ----
     *
     * Two different things arrive here and they must be gated differently.
     *
     *  - `lookDx/lookDy` and `s.sway` are AIM intent: the shooter turned, the gun
     *    lags behind. COD keeps a little of that at ADS, so it survives at 28 %.
     *  - anything periodic at the footstep frequency is LOCOMOTION and must be
     *    gone at ads = 1. The player rig's `viewSway` is a look-lag spring, but it
     *    is fed from the same rig that produces the bob, so it is gated with the
     *    locomotion channels rather than with the aim channels. A sight picture
     *    that breathes with the player's stride is an instant AAA fail.
     */
    const bobGate = 1 - this.ads;               // exactly 0 at full ADS
    let dx = s.lookDx ?? 0, dy = s.lookDy ?? 0;
    if (s.sway) { dx += (s.sway.x ?? 0) * bobGate; dy += (s.sway.y ?? 0) * bobGate; }
    const k = 1 / Math.max(dt, 1e-4);
    this.lagSmooth.x = lerp(this.lagSmooth.x, dx * k, 1 - Math.exp(-18 * dt));
    this.lagSmooth.y = lerp(this.lagSmooth.y, dy * k, 1 - Math.exp(-18 * dt));
    const swayMul = (def.swayScale ?? 1) * (1 - this.ads * 0.72);
    const lx = THREE.MathUtils.clamp(this.lagSmooth.x, -14, 14);
    const ly = THREE.MathUtils.clamp(this.lagSmooth.y, -14, 14);
    this.swayPos.target.set(-lx * 0.0075 * swayMul, ly * 0.0060 * swayMul, 0);
    this.swayRot.target.set(ly * 0.055 * swayMul, -lx * 0.075 * swayMul, lx * 0.055 * swayMul);
    this.swayPos.step(dt); this.swayRot.step(dt);
    this.add.addVec(this.swayPos.x, this.swayRot.x);

    /* ---- additive: bob ---- */
    // Bob goes fully to zero at ads = 1, for the same reason breathing does: a
    // settled sight picture has to be exact, and the player module's bobOffset does
    // not necessarily return to zero when they stop.
    const bobDamp = bobGate;
    if (s.bob) {
      this.bobPos.copy(s.bob).multiplyScalar(bobDamp * (def.bobScale ?? 1));
      this.bobRot.set(-s.bob.y * 4.0, s.bob.x * 3.2, s.bob.x * 5.5).multiplyScalar(bobDamp);
    } else {
      const spd = s.speed ?? 0;
      const amp = THREE.MathUtils.clamp(spd / 4.4, 0, 1.35);
      this.bobPhase += dt * (4.6 + amp * 4.4) * (amp > 0.02 ? 1 : 0);
      const a = amp * bobDamp * (def.bobScale ?? 1);
      this.bobPos.set(
        Math.sin(this.bobPhase) * 0.0125 * a,
        -Math.abs(Math.sin(this.bobPhase * 1.0 + 0.6)) * 0.0090 * a + 0.0045 * a,
        Math.sin(this.bobPhase * 2) * 0.0035 * a,
      );
      this.bobRot.set(
        Math.sin(this.bobPhase * 2 + 1.1) * 0.030 * a,
        Math.sin(this.bobPhase) * 0.045 * a,
        Math.sin(this.bobPhase) * 0.075 * a,
      );
    }
    this.add.addVec(this.bobPos, this.bobRot);

    /* ---- additive: landing / air ---- */
    this.jumpSpring.step(dt);
    this.add.add(0, this.jumpSpring.x * 0.5, 0, this.jumpSpring.x * 1.6, 0, 0);

    /* ---- additive: recoil ---- */
    this.recoilPos.step(dt);
    this.recoilRot.step(dt);
    this.add.addVec(this.recoilPos.x, this.recoilRot.x);

    /* ---- additive: sequence ---- */
    if (this.seq) this._stepSequence(dt, inst);
    this.add.addVec(this.seqOffset.pos, this.seqOffset.rot);

    /* ---- lowered (switching / mantling) ---- */
    if (this.lower > 0.0005) {
      this.add.add(0, -0.10 * this.lower, 0.02 * this.lower, -0.95 * this.lower, 0.25 * this.lower, 0);
    }

    /* ---- compose ---- */
    if (this.rigOverride) {
      this.rig.matrix.copy(this.rigOverride);
      this.rig.matrixWorldNeedsUpdate = true;
      return;
    }
    const pv = this.pivot;
    _e.set(this.add.rot.x, this.add.rot.y, this.add.rot.z, 'XYZ');
    _q.setFromEuler(_e);
    _m.compose(_v.copy(this.add.pos).add(pv), _q, _v2.set(1, 1, 1));
    _m2.makeTranslation(-pv.x, -pv.y, -pv.z);
    _m.multiply(_m2);
    _m2.compose(A.pos, A.quat, _v2.set(1, 1, 1));
    _m.multiply(_m2);
    this.rig.matrix.copy(_m);
    this.rig.matrixWorldNeedsUpdate = true;

    /* ---- viewmodel FOV pull ---- */
    const target = lerp(this.fovBase, def.adsFov ?? 42, smoothstep(this.ads));
    this.fov = lerp(this.fov, target, 1 - Math.exp(-16 * dt));
  }

  /** Place the whole rig at the view camera. */
  syncToCamera(cam) {
    this.root.matrix.copy(cam.matrixWorld);
    this.root.matrixWorldNeedsUpdate = true;
    this.root.updateMatrixWorld(true);
  }

  /* ------------------------------------------------------------------ *
   * Sequences
   * ------------------------------------------------------------------ */

  play(seq) {
    this.seq = seq;
    this.seqT = 0;
    if (seq?.onStart) seq.onStart(this, this.current);
  }

  stopSequence() {
    if (this.seq?.onEnd) this.seq.onEnd(this, this.current);
    this.seq = null;
    this.seqOffset.pos.set(0, 0, 0);
    this.seqOffset.rot.set(0, 0, 0);
    if (this.current) this.current.resetPose();
  }

  get busy() { return !!this.seq; }

  _stepSequence(dt, inst) {
    this.seqT += dt;
    const seq = this.seq;
    this.seqOffset.pos.set(0, 0, 0);
    this.seqOffset.rot.set(0, 0, 0);
    const t = Math.min(this.seqT / seq.duration, 1);
    seq.step(t, this.seqT, this, inst);
    if (this.seqT >= seq.duration) this.stopSequence();
  }

  /* ------------------------------------------------------------------ *
   * Impulses
   * ------------------------------------------------------------------ */

  fireKick(inst, imp) {
    const d = inst.def;
    this.recoilPos.kick(_v.set(
      imp.vYaw * 0.55 * (d.kickLateral ?? 1),
      imp.vPitch * 0.30,
      (d.kickBack ?? 0.30) * (1 - this.ads * 0.35),
    ));
    this.recoilRot.kick(_v.set(
      imp.vPitch * (d.kickPitch ?? 5.2) * (1 - this.ads * 0.32),
      imp.vYaw * (d.kickYaw ?? 3.0),
      imp.vRoll * (d.kickRoll ?? 2.2),
    ));
  }

  land(power) { this.jumpSpring.kick(-Math.min(power, 1.6) * 0.09); }
}
